<template>
  <div class="p-6 min-h-screen">
    <h1 class="text-2xl font-bold mb-4">Gestion des Utilisateurs</h1>

    <!-- User Form for Create/Update -->
    <div class="bg-white shadow-md rounded-lg p-6 mb-6">
      <h2 class="text-xl font-semibold mb-4">{{ editingUser ? "Modifier l'utilisateur" : "Ajouter un utilisateur" }}</h2>
      <form @submit.prevent="handleSubmit">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label for="username" class="block text-sm font-medium text-gray-700">Nom d'utilisateur</label>
            <input
              type="text"
              id="username"
              v-model="userForm.username"
              class="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#ec4400] focus:border-[#ec4400]"
              required
            />
          </div>

          <div>
            <label for="password" class="block text-sm font-medium text-gray-700">Mot de passe</label>
            <input
              type="password"
              id="password"
              v-model="userForm.password"
              class="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#ec4400] focus:border-[#ec4400]"
              :required="!editingUser"
            />
            <p v-if="editingUser" class="text-sm text-gray-500 mt-1">Laisser vide pour ne pas changer le mot de passe.</p>
          </div>
          <div>
            <label for="role" class="block text-sm font-medium text-gray-700">Rôle</label>
            <select
              id="role"
              v-model="userForm.role"
              class="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#ec4400] focus:border-[#ec4400]"
              required
            >
              <option value="user">Utilisateur</option>
              <option value="admin">Admin</option>
              <option value="superadmin">Super Admin</option>
            </select>
          </div>
          <div>
            <label for="company" class="block text-sm font-medium text-gray-700">Société</label>
            <select
              id="company"
              v-model="userForm.company"
              class="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#ec4400] focus:border-[#ec4400]"
              required
            >
              <option value="" disabled>Sélectionner une société</option>
              <option v-for="company in companies" :key="company.id" :value="company.name">
                {{ company.name }}
              </option>
            </select>
          </div>
        </div>
        <div class="mt-6 flex justify-end space-x-3">
          <button
            type="button"
            @click="cancelEdit"
            v-if="editingUser"
            class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#ec4400]"
          >
            Annuler
          </button>
          <button
            type="submit"
            class="px-4 py-2 bg-[#ec4400] text-white rounded-md shadow-sm text-sm font-medium hover:bg-[#b33300] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#ec4400]"
          >
            {{ editingUser ? 'Mettre à jour' : 'Ajouter' }}
          </button>
        </div>
      </form>
    </div>

    <!-- User List Table -->
    <div class="bg-white shadow-md rounded-lg p-6">
      <h2 class="text-xl font-semibold mb-4">Liste des Utilisateurs</h2>
      <p v-if="loading" class="text-center">Chargement des utilisateurs...</p>
      <p v-if="error" class="text-red-600 text-center">{{ error }}</p>

      <table v-if="users.length && !loading" class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              ID
            </th>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Nom d'utilisateur
            </th>

            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Rôle
            </th>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Société
            </th>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="user in users" :key="user.id">
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ user.id }}</td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ user.username }}</td>

            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ user.role }}</td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ user.company }}</td>
            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
              <button @click="editUser(user)" class="text-[#ec4400] hover:text-[#b33300] mr-3">
                Éditer
              </button>
              <button @click="deleteUser(user.id)" class="text-red-600 hover:text-red-900">
                Supprimer
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else-if="!loading" class="text-center text-gray-500">Aucun utilisateur trouvé.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue';
import apiFetch from '@/apiClient';


const users = ref([]);
const companies = ref([]); // New ref for companies
const loading = ref(false);
const error = ref(null);
const editingUser = ref(null); // Stores the user being edited

const userForm = reactive({
  id: null,
  username: '',
  password: '',
  role: 'user',
  company: '', // Add company field
});

async function fetchCompanies() {
  try {
    const data = await apiFetch('/companies');
    companies.value = data;
    // Set default company if none selected and companies are available
    if (!userForm.company && companies.value.length > 0) {
      userForm.company = companies.value[0].name;
    }
  } catch (err) {
    console.error('Error fetching companies:', err);
    // Handle error, maybe set a default or show a message
  }
}

async function fetchUsers() {
  loading.value = true;
  error.value = null;
  try {
    const data = await apiFetch('/auth/users');
    users.value = data;
  } catch (err) {
    console.error('Error fetching users:', err);
    error.value = err.message || 'Échec du chargement des utilisateurs.';
  } finally {
    loading.value = false;
  }
}

async function handleSubmit() {
  error.value = null;
  try {
    if (editingUser.value) {
      // Update existing user
              const payload = {
              username: userForm.username,
              role: userForm.role,
              company: userForm.company, // Include company in update payload
            };      if (userForm.password) {
        payload.password = userForm.password;
      }
      await apiFetch(`/auth/users/${editingUser.value.id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      console.log('User updated successfully');
    } else {
      // Create new user (register)
      await apiFetch('/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          username: userForm.username,
          password: userForm.password,
          role: userForm.role,
          company: userForm.company, // Include company in register payload
        }),
      });
      console.log('User created successfully');
    }
    resetForm();
    await fetchUsers(); // Refresh list
  } catch (err) {
    console.error('Error submitting user form:', err);
    error.value = err.message || 'Échec de l\'opération utilisateur.';
  }
}

function editUser(user) {
  editingUser.value = user;
  userForm.id = user.id;
  userForm.username = user.username;
  userForm.role = user.role;
  userForm.company = user.company; // Set company when editing
  userForm.password = ''; // Clear password field for editing
}

function cancelEdit() {
  editingUser.value = null;
  resetForm();
}

async function deleteUser(id) {
  if (!confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
    return;
  }
  loading.value = true;
  error.value = null;
  try {
    await apiFetch(`/auth/users/${id}`, {
      method: 'DELETE',
    });
    console.log('User deleted successfully');
    await fetchUsers(); // Refresh list
  } catch (err) {
    console.error('Error deleting user:', err);
    error.value = err.message || 'Échec de la suppression de l\'utilisateur.';
  } finally {
    loading.value = false;
  }
}

function resetForm() {
  userForm.id = null;
  userForm.username = '';
  userForm.password = '';
  userForm.role = 'user';
  userForm.company = companies.value.length > 0 ? companies.value[0].name : ''; // Reset company
  editingUser.value = null;
}

onMounted(() => {
  fetchCompanies(); // Fetch companies when component mounts
  fetchUsers();
});
</script>

<style scoped>
/* Scoped styles for this component */
</style>